/**
 * Story 2.2 — the frontend's half of AD-14's fixed list-query grammar
 * (`app/api/query.py` is the backend half and the source of truth):
 * `?sort=field` / `?sort=-field`, `&field=value` filters, `&page=`. Pure
 * functions operating on plain strings/`URLSearchParams`, so they are
 * testable without a DOM or a React tree — the shared table's URL-reading
 * and URL-writing hooks (`components/table/DataTable.tsx`) are the only
 * callers, and they must never invent a second spelling of any of this.
 *
 * `page_size` is deliberately not part of this module's URL grammar: it is
 * a viewport-derived default (50 desktop / 25 phone, DESIGN.md § Layout &
 * Spacing), not something this story exposes a control to change, so it is
 * computed by `useIsPhone` at the call site and threaded straight into the
 * fetch, never read back out of the URL.
 */

export const SORT_PARAM = "sort";
export const PAGE_PARAM = "page";
//: Not part of this module's URL grammar (see the header comment) — named
//: here so `appendUnknownParams` can recognise and skip it explicitly,
//: rather than treating a stored `page_size` as an "unknown" param to
//: forward. `buildListSearch` is the only writer of this key.
export const PAGE_SIZE_PARAM = "page_size";

export const DESKTOP_PAGE_SIZE = 50;
export const PHONE_PAGE_SIZE = 25;

export interface ParsedSort {
  field: string;
  descending: boolean;
}

/** `"field"` or `"-field"` (or `null`/absent) from the URL's `sort` param. */
export function parseSortParam(raw: string | null): ParsedSort | null {
  if (raw === null || raw === "") {
    return null;
  }
  const descending = raw.startsWith("-");
  const field = descending ? raw.slice(1) : raw;
  if (field === "") {
    return null;
  }
  return { field, descending };
}

/** The `sort` param's own AD-14 spelling, for building a fetch query string. */
export function serialiseSortParam(sort: ParsedSort | null): string | null {
  if (sort === null) {
    return null;
  }
  return sort.descending ? `-${sort.field}` : sort.field;
}

/**
 * The three-state click cycle a sortable column header follows (Boundaries &
 * Constraints, I/O matrix): unsorted -> ascending -> descending -> unsorted.
 * Clicking a *different* column header always starts that column at
 * ascending, replacing whatever was sorted before rather than accumulating.
 */
export function nextSortParam(current: string | null, field: string): string | null {
  const parsed = parseSortParam(current);
  if (parsed === null || parsed.field !== field) {
    return field;
  }
  if (!parsed.descending) {
    return `-${field}`;
  }
  return null;
}

/**
 * The recognised `&field=value` filters currently on the URL, restricted to
 * `knownFields` — a field the caller's column/filter config never declared
 * is not this list's concern to parse (mirrors the backend's own
 * `parse_filters` refusing an unknown parameter rather than silently
 * dropping it; the frontend's job here is narrower — just don't invent
 * filters nobody configured).
 */
export function parseFilterParams(
  searchParams: URLSearchParams,
  knownFields: readonly string[],
): Record<string, string> {
  const filters: Record<string, string> = {};
  for (const field of knownFields) {
    const value = searchParams.get(field);
    if (value !== null && value !== "") {
      filters[field] = value;
    }
  }
  return filters;
}

/** `page=` from the URL, defaulting to 1 and never below it. */
export function parsePageParam(raw: string | null): number {
  if (raw === null) {
    return 1;
  }
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
}

/** 50 on desktop, 25 on phone (DESIGN.md § Layout & Spacing) — no third size. */
export function pageSizeFor(isPhone: boolean): number {
  return isPhone ? PHONE_PAGE_SIZE : DESKTOP_PAGE_SIZE;
}

/**
 * Every param on the current URL that a route's own known grammar (sort,
 * page, and its declared filterable fields) doesn't already account for —
 * forwarded onto `search`, not dropped, so `app/api/query.py`'s own
 * `reject_unknown_parameters` can refuse it by name server-side instead of
 * the frontend silently pretending the request never named it.
 *
 * Exists because `parseFilterParams` above deliberately restricts itself to
 * known fields — right for *rendering* (which filter chips to draw for a
 * value the UI understands) but wrong for *fetching*: a saved view (Story
 * 2.4) selected after a column config changed carries a filter field the
 * frontend no longer recognises, and that has to reach the server to be
 * refused with a plain-language message, never be stripped before the
 * request is even sent (`app/api/query.py`'s own comment on this exact
 * case: "a typo in a saved view's URL would quietly show every row").
 *
 * `page_size` is deliberately skipped, not forwarded: this module's own
 * header comment says it is "never read back out of the URL" — it is
 * computed once, per viewport, at the call site (`pageSizeFor`) and always
 * written by `buildListSearch`. A saved view whose stored string happens to
 * carry `page_size=200` (e.g. a stale view saved before a page-size change,
 * or crafted by hand) must not silently override that computed value —
 * unlike an unrecognised *filter*, a stray `page_size` changes only how
 * many rows come back per request, never which rows, so overwriting it with
 * the real one is a safe normalisation rather than a second silent-drop bug.
 */
export function appendUnknownParams(
  search: string,
  searchParams: URLSearchParams,
  knownFilterableFields: readonly string[],
): string {
  const params = new URLSearchParams(search);
  for (const [key, value] of searchParams.entries()) {
    if (
      key === SORT_PARAM ||
      key === PAGE_PARAM ||
      key === PAGE_SIZE_PARAM ||
      knownFilterableFields.includes(key)
    ) {
      continue;
    }
    params.set(key, value);
  }
  return params.toString();
}

/**
 * The exact query string the fetch layer sends for a given list state,
 * matching `app/api/query.py`'s grammar field for field — nothing here
 * renames or reshapes it.
 */
export function buildListSearch(state: {
  sort: ParsedSort | null;
  filters: Record<string, string>;
  page: number;
  pageSize: number;
}): string {
  const params = new URLSearchParams();
  const sortValue = serialiseSortParam(state.sort);
  if (sortValue !== null) {
    params.set(SORT_PARAM, sortValue);
  }
  for (const [field, value] of Object.entries(state.filters)) {
    params.set(field, value);
  }
  if (state.page > 1) {
    params.set(PAGE_PARAM, String(state.page));
  }
  params.set(PAGE_SIZE_PARAM, String(state.pageSize));
  return params.toString();
}
